import React from 'react';
import { CFT160, CFT161, t20aApi } from '../../../services/t20aApi';

interface Tab1Props {
  formData: Partial<CFT160>;
  names: Record<string, string>;
  isEditing: boolean;
  getField: (key: keyof CFT160) => CFT160[keyof CFT160];
  setField: (key: keyof CFT160, value: CFT160[keyof CFT160]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  attendees: Partial<CFT161>[];
  addAttendeeRow: () => void;
  deleteAttendeeRow: (idx: number) => void;
  setAttendeeField: (idx: number, key: keyof CFT161, value: CFT161[keyof CFT161]) => void;
  selectedAttendeeIdx: number;
  setSelectedAttendeeIdx: (idx: number) => void;
}

/** Tab 1: Basic Info + Internal Attendees Grid (Page1 from ui_spec) */
const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, getField, setField, resolveSingleName,
  attendees, addAttendeeRow, deleteAttendeeRow, setAttendeeField,
  selectedAttendeeIdx, setSelectedAttendeeIdx,
}) => (
  <div>
    {/* Basic Info Fields */}
    <div className="form-grid">
      <label>會議地點</label>
      <div style={{ gridColumn: 'span 3' }}>
        <input
          type="text"
          value={String(getField('ft160_16') || '')}
          onChange={e => setField('ft160_16', e.target.value)}
          disabled={!isEditing}
          maxLength={40}
          style={{ width: '100%' }}
        />
      </div>

      <label>開始時間</label>
      <input
        type="text"
        value={String(getField('ft160_14') || '')}
        onChange={e => setField('ft160_14', e.target.value)}
        disabled={!isEditing}
        maxLength={8}
        placeholder="HH:MM:SS"
        style={{ width: 120 }}
      />

      <label>結束時間</label>
      <input
        type="text"
        value={String(getField('ft160_15') || '')}
        onChange={e => setField('ft160_15', e.target.value)}
        disabled={!isEditing}
        maxLength={8}
        placeholder="HH:MM:SS"
        style={{ width: 120 }}
      />

      <label>主席</label>
      <div className="lookup-field">
        <input
          type="text"
          value={String(getField('ft160_17') || '')}
          onChange={e => setField('ft160_17', e.target.value)}
          onBlur={() => resolveSingleName('chairman', 'employee', String(getField('ft160_17') || ''))}
          disabled={!isEditing}
          style={{ width: 100 }}
        />
        <span className="lookup-name">{names.chairman || String(getField('ft160_20') || '')}</span>
      </div>

      <label>記錄人</label>
      <div className="lookup-field">
        <input
          type="text"
          value={String(getField('ft160_21') || '')}
          onChange={e => setField('ft160_21', e.target.value)}
          onBlur={() => resolveSingleName('recorder', 'employee', String(getField('ft160_21') || ''))}
          disabled={!isEditing}
          style={{ width: 100 }}
        />
        <span className="lookup-name">{names.recorder || String(getField('ft160_22') || '')}</span>
      </div>

      <label>人數</label>
      <input
        type="number"
        value={formData.ft160_18 ?? 0}
        readOnly
        style={{ width: 80 }}
      />

      <div />
      <div />
    </div>

    {/* Description editbox (ft160_12) */}
    <div style={{ marginTop: 12 }}>
      <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>說明</label>
      <textarea
        value={String(getField('ft160_12') || '')}
        onChange={e => setField('ft160_12', e.target.value)}
        disabled={!isEditing}
        rows={3}
        style={{ width: '100%' }}
      />
    </div>

    {/* Attendees Grid (CFT161) — Internal people */}
    <div className="table-wrapper" style={{ marginTop: 16 }}>
      <div className="table-toolbar">
        <div className="left">
          <span style={{ fontWeight: 500, fontSize: 13 }}>內部與會人員</span>
        </div>
        <div className="right">
          <button className="btn btn-sm" disabled={!isEditing} onClick={addAttendeeRow}>明細新增</button>
          <button className="btn btn-sm btn-danger" disabled={!isEditing || attendees.length === 0}
            onClick={() => deleteAttendeeRow(selectedAttendeeIdx)}>明細刪除</button>
        </div>
      </div>
      <div className="table-scroll" style={{ maxHeight: 240 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>序號</th>
              <th style={{ width: 90 }}>員工代號</th>
              <th style={{ width: 100 }}>中文姓名</th>
              <th style={{ width: 160 }}>英文姓名</th>
              <th style={{ width: 140 }}>部門名稱</th>
            </tr>
          </thead>
          <tbody>
            {attendees.map((row, idx) => (
              <tr
                key={idx}
                className={idx === selectedAttendeeIdx ? 'selected' : ''}
                onClick={() => setSelectedAttendeeIdx(idx)}
              >
                <td>{row.ft161_02}</td>
                <td>
                  <input
                    type="text"
                    value={row.ft161_11 || ''}
                    onChange={e => setAttendeeField(idx, 'ft161_11', e.target.value)}
                    onBlur={async () => {
                      const code = row.ft161_11 || '';
                      if (!code) return;
                      const [empName, empNameEn, deptName] = await Promise.all([
                        t20aApi.lookupName('employee', code),
                        t20aApi.lookupName('employee_en', code),
                        t20aApi.lookupName('employee_dept', code),
                      ]);
                      setAttendeeField(idx, 'ft161_11a', empName);
                      setAttendeeField(idx, 'ft161_11b', empNameEn);
                      setAttendeeField(idx, 'ft161_12a', deptName);
                    }}
                    disabled={!isEditing}
                    style={{ width: '100%', height: 28, fontSize: 13 }}
                  />
                </td>
                <td>{row.ft161_11a || ''}</td>
                <td>{row.ft161_11b || ''}</td>
                <td>{row.ft161_12a || ''}</td>
              </tr>
            ))}
            {attendees.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.25)' }}>無資料</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
));

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
